<script setup lang="ts">
/* =============================================================
   MAP VIEW
   =============================================================
   The Vue side of lib/map.ts: owns the container element and the
   map's lifetime, and forwards a marker click as an event.

   The Leaflet instance is kept in a plain variable rather than a
   ref on purpose — Vue's reactive proxy would wrap Leaflet's
   internals, and a proxied map breaks in ways that are very hard
   to read. Nothing here needs it to be reactive.
   ============================================================= */

import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import { createMap } from '@/lib/map'
import type { MapHandle, MapPoint } from '@/lib/map'

const props = withDefaults(
  defineProps<{
    points: MapPoint[]
    /** CSS height. The map has no intrinsic size and collapses without one. */
    height?: string
    /** Clicking the map emits `pick` with the position -- the location form's pin picker. */
    pickable?: boolean
    /** Wheel zooms the map. Leave off when the map sits inside a scrolling page. */
    wheelZoom?: boolean
  }>(),
  { height: '32rem', pickable: false, wheelZoom: false },
)

const emit = defineEmits<{ select: [point: MapPoint]; pick: [lat: number, lng: number] }>()

const el = useTemplateRef<HTMLDivElement>('el')
const failed = ref(false)

let map: MapHandle | null = null
let observer: ResizeObserver | null = null

onMounted(() => {
  if (!el.value) return
  try {
    map = createMap(el.value, {
      onSelect: (point) => emit('select', point),
      onPick: props.pickable ? (lat, lng) => emit('pick', lat, lng) : undefined,
      scrollWheelZoom: props.wheelZoom,
    })
    map.setPoints(props.points)
  } catch {
    // A map that cannot start must not take the page with it -- the list
    // beside it still works.
    failed.value = true
    return
  }

  // The container can change width (sidebar, window, a layout switch),
  // and Leaflet only recalculates its tile grid when told. A callback can
  // still be delivered after disconnect(), which is why `resize` is a no-op
  // on a destroyed map rather than relying on this to stop first.
  observer = new ResizeObserver(() => map?.resize())
  observer.observe(el.value)
})

watch(
  () => props.points,
  (points) => map?.setPoints(points),
  { deep: true },
)

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
  map?.destroy()
  map = null
})
</script>

<template>
  <div class="wrap" :style="{ height }">
    <p v-if="failed" class="failed">The map could not be loaded.</p>
    <div v-show="!failed" ref="el" class="map" />
  </div>
</template>

<style scoped>
.wrap {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: var(--radius-md);
}

.map {
  width: 100%;
  height: 100%;
  background-color: var(--color-surface-sunken);
}

.failed {
  display: grid;
  place-items: center;
  height: 100%;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
</style>

<!-- Leaflet builds its own DOM — panes, controls, tooltips, and the divIcon
     from lib/map.ts — none of which carries this component's scope id, so
     these rules are global by necessity. They are namespaced under .leaflet-*
     and .ra-pin to keep that honest. -->
<style>
.ra-pin {
  display: grid;
  place-items: center;
  background: none;
  border: 0;
}

.ra-pin__dot {
  width: 14px;
  height: 14px;
  background-color: var(--color-accent);
  border: 2px solid var(--color-surface);
  border-radius: var(--radius-full);
  box-shadow: 0 1px 4px rgb(4 18 29 / 45%);
  transition:
    transform var(--dur-fast) var(--ease),
    background-color var(--dur-fast) var(--ease);
}

.ra-pin:hover .ra-pin__dot,
.ra-pin:focus-visible .ra-pin__dot {
  transform: scale(1.35);
  background-color: var(--color-accent-2);
}

.ra-pin:focus-visible {
  outline: none;
}

.ra-pin--muted .ra-pin__dot {
  background-color: var(--color-text-faint);
}

.ra-pin--selected .ra-pin__dot {
  width: 18px;
  height: 18px;
  background-color: var(--color-accent-2);
}

/* Price pills: the search map's pins. The Leaflet box is fixed-size; the
   pill inside is centred in it and sized by its text. */
.ra-pill {
  display: grid;
  place-items: center;
  background: none;
  border: 0;
}

.ra-pill__text {
  padding: 0.2rem 0.55rem;
  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--color-accent-contrast);
  background-color: var(--color-accent);
  border: 2px solid var(--color-surface);
  border-radius: var(--radius-full);
  box-shadow: 0 1px 4px rgb(4 18 29 / 35%);
  transition: transform var(--dur-fast) var(--ease);
}

.ra-pill:hover .ra-pill__text,
.ra-pill:focus-visible .ra-pill__text {
  transform: scale(1.08);
}

.ra-pill:focus-visible {
  outline: none;
}

.ra-pill--muted .ra-pill__text {
  color: var(--color-text-muted);
  background-color: var(--color-surface-sunken);
  box-shadow: none;
}

.ra-pill--selected .ra-pill__text {
  color: var(--rv-navy-900, var(--color-heading));
  background-color: var(--color-accent-2);
  transform: scale(1.12);
}

.ra-picking {
  cursor: crosshair;
}

.ra-pin:focus-visible .ra-pin__dot {
  box-shadow: var(--shadow-focus);
}

@media (prefers-reduced-motion: reduce) {
  .ra-pin__dot,
  .ra-pin:hover .ra-pin__dot {
    transition: none;
    transform: none;
  }
}

/* Leaflet's defaults are Bootstrap-era; these pull the chrome onto our
   tokens so the map does not look like a different product. */
.leaflet-container {
  font-family: var(--font-sans);
  background-color: var(--color-surface-sunken);
}

.leaflet-bar a,
.leaflet-bar a:hover {
  color: var(--color-heading);
  background-color: var(--color-surface);
  border-bottom-color: var(--color-border);
}

.leaflet-bar a:hover {
  background-color: var(--color-surface-hover);
}

.leaflet-touch .leaflet-bar,
.leaflet-touch .leaflet-control-attribution {
  border-color: var(--color-border);
}

.leaflet-control-attribution {
  font-size: var(--text-xs);
  color: var(--color-text-faint);
  background-color: color-mix(in srgb, var(--color-surface) 88%, transparent);
}

.leaflet-control-attribution a {
  color: var(--color-text-muted);
}

.leaflet-tooltip {
  padding: var(--space-3xs) var(--space-2xs);
  font-size: var(--text-xs);
  line-height: var(--leading-snug);
  color: var(--color-text);
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
}

.leaflet-tooltip strong {
  color: var(--color-heading);
}

.leaflet-tooltip-top::before {
  border-top-color: var(--color-border);
}

.leaflet-marker-icon {
  cursor: pointer;
}
</style>
